import { io, Socket } from "socket.io-client";

const WS_URL = process.env.REACT_APP_API_SOCKET_URL;

class SocketHandler {
  private socket: Socket | null = null;
  private static instance: SocketHandler;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private subscribedEvents: Set<string> = new Set();

  // Constructor no longer auto-connects. Callers must invoke connect() after
  // confirming authentication so the socket is never opened for guest users
  // and the auth token is always present in the handshake.
  private constructor() {}

  public static getInstance(): SocketHandler {
    if (!SocketHandler.instance) {
      SocketHandler.instance = new SocketHandler();
    }
    return SocketHandler.instance;
  }

  public connect(): void {
    if (this.socket?.connected) return;

    // Tear down any previous disconnected socket before creating a new one
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    this.initializeSocket();
  }

  private initializeSocket() {
    // Read the auth token the same way axiosConfig.ts does — it is guaranteed
    // to be present because connect() is only called from auth-guarded effects.
    const token = localStorage.getItem("access_token");

    this.socket = io(WS_URL!, {
      transports: ["websocket"],
      auth: token ? { token } : {},
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,      // initial backoff: 1 s
      reconnectionDelayMax: 30000,  // cap backoff at 30 s on bad networks
      randomizationFactor: 0.5,     // ±50 % jitter to avoid thundering herd
    });

    this.socket.on("connect", () => {});

    this.socket.on("disconnect", () => {});

    this.socket.on("connect_error", (error: Error) => {
      console.error("Socket.IO connection error:", error.message);
    });
  }

  public subscribeToWhiteBoard(cardId: string, callback: (data: any) => void) {
    const eventName = `on-card-message-received-${cardId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (cardId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromWhiteboard(bookingId: string) {
    const eventName = `on-card-message-received-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToInstantWhiteBoard(
    bookingId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-instant-card-message-received-${bookingId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromInstantWhiteboard(bookingId: string) {
    const eventName = `on-instant-card-message-received-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToBooking(bookingId: string, callback: (data: any) => void) {
    const eventName = `on-message-received-${bookingId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromBooking(bookingId: string) {
    const eventName = `on-message-received-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }
  public subscribeToPVLC(cardId: string, callback: (data: any) => void) {
    const eventName = `on-card-message-received-${cardId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (cardId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromPVLC(cardId: string) {
    const eventName = `on-card-pvlc-status-received-${cardId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToNotification(
    userId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-notification-received-${userId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        console.log("notification data", data);
        if (userId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }


  public unsubscribeFromNotification(userId: string) {
    const eventName = `on-notification-received-${userId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }


  public subscribeToStaiMomentNotification(
    postId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-stai-moment-${postId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        console.log("notification data", data);
        if (postId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }
  
  public unsubscribeFromStaiMomentNotification(postId: string) {
    const eventName = `on-stai-moment-${postId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }



  public subscribeToTaanaConversation(
    userId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-taana-message-received-${userId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        console.log("taana conversation data", data);
        if (userId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromTaanaConversation(userId: string) {
    const eventName = `on-taana-message-received-${userId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToYoutubeUrlReceived(
    bookingId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-youtube-url-received-${bookingId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeToYoutubeUrlReceived(bookingId: string) {
    const eventName = `on-youtube-url-received-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToYouTubeRTMP(
    receiverId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-youtube-rtmp-received-${receiverId}`;
    this.subscribeToCustomEvent(eventName, callback);
  }

  public unsubscribeFromYouTubeRTMP(receiverId: string) {
    const eventName = `on-youtube-rtmp-received-${receiverId}`;
    this.unsubscribeFromCustomEvent(eventName);
  }

  // ---------------------------------------------------------------------------
  // Flash Card lyrics (AudioMix karaoke)
  // Backend emits `on-flash-card-lyrics-{id}` with payload
  // containing at least { lyrics_url: string } (or similar).
  // ---------------------------------------------------------------------------
  public subscribeToFlashLyrics(
    flashCardId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-flash-card-lyrics-${flashCardId}`;
    this.subscribeToCustomEvent(eventName, callback);
  }

  public unsubscribeFromFlashLyrics(flashCardId: string) {
    const eventName = `on-flash-card-lyrics-${flashCardId}`;
    this.unsubscribeFromCustomEvent(eventName);
  }

  public subscribeToBuskingSession(
    cardId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-busking-session-${cardId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (cardId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeToBuskingSession(cardId: string) {
    const eventName = `on-busking-session-${cardId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToLiveAvatarPresentation(
    cardId: string,
    callback: (data: any) => void,
  ) {
    const eventName = `on-question-to-avatar-${cardId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (cardId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeToLiveAvatarPresentation(talentId: string) {
    const eventName = `on-question-to-avatar-${talentId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  // Generic subscribe/unsubscribe helpers for custom event names
  public subscribeToCustomEvent(
    eventName: string,
    callback: (data: any) => void,
  ) {
    if (!eventName) {
      console.warn(
        "[SocketHandler] subscribeToCustomEvent: eventName is empty",
      );
      return;
    }

    console.log("[SocketHandler] subscribeToCustomEvent:", {
      eventName,
      socketExists: !!this.socket,
      socketConnected: this.socket?.connected,
      alreadySubscribed: this.subscribedEvents.has(eventName),
    });

    // Always update the handler (in case callback changed)
    this.messageHandlers.set(eventName, callback);

    if (this.socket) {
      // If already subscribed, remove old listener first to avoid duplicates
      if (this.subscribedEvents.has(eventName)) {
        console.log(
          "[SocketHandler] Event already subscribed, removing old listener:",
          eventName,
        );
        this.socket.off(eventName);
      }

      // Add new listener
      this.socket.on(eventName, (data: any) => {
        console.log("[SocketHandler] Event received:", { eventName, data });
        const handler = this.messageHandlers.get(eventName);
        if (handler) {
          handler(data);
        } else {
          console.warn(
            "[SocketHandler] No handler found for event:",
            eventName,
          );
        }
      });
      this.subscribedEvents.add(eventName);
      console.log("[SocketHandler] Successfully subscribed to:", eventName);
    } else {
      console.error("[SocketHandler] Cannot subscribe: socket is null");
    }
  }

  public unsubscribeFromCustomEvent(eventName: string) {
    if (!eventName) return;
    this.messageHandlers.delete(eventName);

    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.messageHandlers.clear();
    this.subscribedEvents.clear();
  }
}

export default SocketHandler;
